import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuthState } from '@/hooks/useAuthState';
import { useReadinessAssessment } from '@/hooks/useReadinessAssessment';
import AssessmentContainer from '@/features/LegacyBenchmarksUI/components/readiness/AssessmentContainer';
import { readinessQuestions } from '@/data/readiness';
import { Loader2 } from 'lucide-react';
import InsightsDisplay from '@/features/LegacyBenchmarksUI/components/readiness/InsightsDisplay';
import { storePendingAssessment } from '@/utils/assessmentStorage';
import { ROUTES } from '@/config/routes';

const ReadinessAssessmentWizard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { user } = useAuthState();
  const [loading, setLoading] = useState(true);
  const view = searchParams.get('view');
  
  const {
    currentDimension,
    setCurrentDimension,
    answers,
    setAnswers,
    hasChanges,
    dimensionAnswers,
    handleAnswerChange,
    handleSave,
  } = useReadinessAssessment();

  console.log('ReadinessAssessmentWizard render:', { 
    currentDimension, 
    answers, 
    dimensionAnswers,
    hasChanges,
    user 
  });

  useEffect(() => {
    const initializeAssessment = async () => {
      try {
        const savedAnswers = location.state?.savedAnswers;
        console.log('Initializing with saved answers:', savedAnswers);
        
        if (savedAnswers && Object.keys(savedAnswers).length > 0) {
          console.log('Setting saved answers:', savedAnswers);
          setAnswers(prev => ({...prev, ...savedAnswers}));
        }
        setLoading(false);
      } catch (error) {
        console.error('Error initializing assessment:', error);
        setLoading(false);
      }
    };

    initializeAssessment();
  }, [location.state?.savedAnswers, setAnswers]);

  const handleNext = () => {
    if (currentDimension < readinessQuestions.length - 1) {
      setCurrentDimension(currentDimension + 1);
    } else {
      handleComplete();
    }
  };

  const handleComplete = () => {
    console.log('Navigating to dashboard with answers:', answers);
    navigate(ROUTES.READINESS_ASSESSMENT.DASHBOARD, { 
      state: { answers }
    });
  };

  const handlePrevious = () => {
    if (currentDimension > 0) {
      setCurrentDimension(currentDimension - 1);
    }
  };

  const handleSaveProgress = async () => {
    if (!user) {
      storePendingAssessment({
        answers,
        currentDimension,
        returnTo: ROUTES.READINESS_ASSESSMENT.WIZARD
      });
      navigate('/login');
      return;
    }
    
    await handleSave();
  };

  const isCurrentDimensionComplete = Object.values(dimensionAnswers).length === 
    readinessQuestions[currentDimension].questions.length;

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (view === 'results' && Object.keys(answers).length > 0) {
    return <InsightsDisplay responses={answers} />;
  }

  return (
    <AssessmentContainer
      currentDimension={currentDimension}
      dimensionAnswers={dimensionAnswers}
      hasChanges={hasChanges}
      onAnswerChange={handleAnswerChange}
      onNext={handleNext}
      onPrevious={handlePrevious}
      onSave={handleSaveProgress}
      isCurrentDimensionComplete={isCurrentDimensionComplete}
    />
  );
};

export default ReadinessAssessmentWizard;
